// ui/presenters/authorizationRowPresenter.js
import { i18n } from 'boot/i18n';

import { formatCalendarDate } from 'src/utils/DateUtils';

import { isShared } from '../../domain/model/ApprovedLimit';
import { authorizationAttention } from './authorizationAttention';

/**
 * Presenter: turns an Authorization into the view model one list row renders.
 *
 * THE SUMMARY IS THE LETTER READ BACK, and it converts NOTHING. "1,040 units" is not rendered as
 * "260 hours" even where the code's basis would allow it: the conversion is consumption's, and doing
 * it here would present a derived number as the thing the funder wrote.
 *
 * Shared with the editor rather than reimplemented there, so the string a biller checks against the
 * paper is the same one whether the limit is open, closed or already recorded.
 */

/**
 * One cap as a sentence, or null when the figure is not fully stated yet.
 *
 * A SAVED cap always carries all three facts, but this also renders DRAFTS. Both `period` and
 * `statedUnit` index into a message key, so a null would ask vue-i18n for
 * `billingAuthorizations.cap.null` and render the key itself at the user. Skipped rather than given
 * a placeholder, because the caller decides what an empty summary should say.
 */
const capText = (cap) => {
  if (cap.figure === null || cap.statedUnit === null || cap.period === null) {
    return null;
  }

  return i18n.t(`billingAuthorizations.cap.${cap.period}`, {
    figure: i18n.n(cap.figure),
    unit: i18n.t(`billingAuthorizations.statedUnit.${cap.statedUnit}`),
  });
};

/**
 * One limit as a sentence. Settings are named only on a RESTRICTED limit — "any setting" is the
 * ordinary case, and saying so on every row would bury the one where the scope actually narrows.
 *
 * @param {import('../../domain/model/ApprovedLimit').ApprovedLimit} limit
 * @param {Map<string, string>} treatmentServiceNameById
 * @returns {{ services: string, summary: string, isShared: boolean }}
 */
export function presentLimit(limit, treatmentServiceNameById) {
  const services = limit.services
    .map(({ treatmentServiceId }) => {
      return treatmentServiceNameById.get(treatmentServiceId)
        ?? i18n.t('billingAuthorizations.unknownService');
    })
    .join(i18n.t('billingAuthorizations.serviceSeparator'));

  const stated = limit.caps.map(capText).filter((text) => text !== null);

  // COUNTED, NOT LISTED, and phrased exactly as the chip inside the entry phrases it: raw codes
  // grow without bound, and one fact described two ways reads as two facts. With nothing stated
  // there is no sentence to scope, so the clause is dropped rather than left dangling.
  const scoped = limit.placeOfServiceCodes.length;
  const summary = (stated.length > 0 && scoped > 0
    ? stated.concat([
      i18n.tc('billingAuthorizations.placeOfServiceCount', scoped, { count: scoped }),
    ])
    : stated
  ).join(i18n.t('billingAuthorizations.capSeparator'));

  return { services, summary, isShared: isShared(limit) };
} // presentLimit()

/**
 * @param {import('../../domain/model/Authorization.types').Authorization} authorization
 * @param {Object} context
 * @param {Map<string, string>} context.fundingSourceNameById
 * @param {Map<string, string>} context.treatmentServiceNameById
 * @param {string} context.asOf - 'YYYY-MM-DD'
 * @returns {Object} the row view model
 */
export function presentAuthorizationRow(authorization, context) {
  const { fundingSourceNameById, treatmentServiceNameById, asOf } = context;
  const attention = authorizationAttention(authorization, asOf);
  // Both bounds or neither. One unreadable bound would render a dangling "1 Sep 2026 to ", which
  // reads as a formatting bug rather than as the missing date it is.
  const start = formatCalendarDate(authorization.period.start);
  const end = formatCalendarDate(authorization.period.end);
  const period = start === null || end === null
    ? null
    : i18n.t('billingAuthorizations.period', { start, end });

  return {
    authorizationId: authorization.authorizationId,
    // Unknown rather than blank: a blank row reads as a rendering bug, where the real cause is a
    // payer removed from the clinic's list after the letter was recorded.
    fundingSourceName: fundingSourceNameById.get(authorization.fundingSourceId)
      ?? i18n.t('billingAuthorizations.unknownFundingSource'),
    authorizationNumber: authorization.authorizationNumber,
    period,
    // The separator earns its place only with both halves. A pending letter has no number and an
    // unreadable bound leaves no period; whichever is missing is reported by the attention token
    // instead, where a dangling "·" would read as a rendering fault.
    summary: period === null || authorization.authorizationNumber === null
      ? period ?? authorization.authorizationNumber
      : i18n.t('billingAuthorizations.rowSummary', {
        period,
        number: authorization.authorizationNumber,
      }),
    attention,
    // Quiet by default: a label on every row is what makes the ones needing attention invisible.
    attentionLabel: attention === null
      ? null
      : i18n.t(`billingAuthorizations.attention.${attention}`),
    limits: authorization.limits.map((limit) => presentLimit(limit, treatmentServiceNameById)),
  };
} // presentAuthorizationRow()
